import React, { useRef, useState } from 'react';
import { Tooltip } from '@material-ui/core';

import CustomDropdown from '@components/CustomDropdown';

import {
	changeDateFormat,
	convertDateToLocal,
	getShortName,
	isValidInput,
	onMultipleFileUpload,
	printApiError,
	validateFloatNumber,
	validateNumber
} from '@utils/Util';

import useCostingOptionData from './useCostingOptionData';

const DESELECT_FACTORY_LABEL = 'Deselect Factory';

const RE_COSTING_STEPS = [
	{ value: 'FIRST_RE_COSTING', label: '1st Re-costing done' },
	{ value: 'SECOND_RE_COSTING', label: '2nd Re-costing done' },
	{ value: 'THIRD_RE_COSTING', label: '3rd Re-costing done' }
];

const FINAL_PO_COSTING_OPTION = { value: 'FINAL_PO_COSTING', label: 'Final PO costing done' };

const STEP_LABEL_MAP = {
	INITIAL_COSTING: 'Initial',
	FIRST_RE_COSTING: '1st',
	SECOND_RE_COSTING: '2nd',
	THIRD_RE_COSTING: '3rd',
	FINAL_PO_COSTING: 'PO'
};

const getStepValue = (step) =>
	typeof step === 'object' && step !== null ? (step.constant ?? step.value) : step;

/**
 * Content of a single costing option tab (OP-1, OP-2, ...).
 * Works exactly like the base costing, only the costing of the selected
 * option is updated through its own `initialCostingId`.
 */
const CostingOptionPanel = ({
	option,
	productId,
	suppliers = [],
	onSupplierSearch,
	readOnly = false,
	isShowRecostingSection = false
}) => {
	const prevTarget = useRef(null);
	const [isEditDetails, setIsEditDetails] = useState(false);
	const {
		inputData,
		costList,
		documents,
		leadTime,
		selectedSupplier,
		costingHistory,
		saveRecostingHistory,
		handleChange,
		handleCostingChange,
		handleLeadTime,
		handleRemarks,
		saveBaseSize,
		saveFabric,
		saveCosting,
		saveTargetPrice,
		saveLeadTime,
		saveRemarks,
		saveSupplier,
		onUploadDocument,
		removeDocumentById
	} = useCostingOptionData(option?.initialCostingId, productId);

	const resetAll = () => {
		prevTarget.current = null;
	};

	const completedReCostingSteps = new Set(costingHistory.map((item) => getStepValue(item?.recostingStep)));
	const isPoCostingDone = completedReCostingSteps.has(FINAL_PO_COSTING_OPTION.value);
	const nextReCostingStep = RE_COSTING_STEPS.find((step) => !completedReCostingSteps.has(step.value));
	const reCostingOptions = isPoCostingDone
		? []
		: [nextReCostingStep, FINAL_PO_COSTING_OPTION].filter(Boolean);
	const isReCostingDisabled = readOnly || isPoCostingDone;

	const handleDownloadFile = async (document) => {
		try {
			const response = await fetch(document.docUrl);
			const blob = await response.blob();
			const url = window.URL.createObjectURL(blob);
			const link = window.document.createElement('a');
			link.href = url;
			link.setAttribute('download', document.name);
			window.document.body.appendChild(link);
			link.click();
			link.parentNode.removeChild(link);
			window.URL.revokeObjectURL(url);
		} catch (error) {
			printApiError(error);
		}
	};

	const renderDocumentList = () =>
		documents?.map((item) => (
			<div className="file-chip d-flex align-items-center" key={`option_document_${item.id}`}>
				<Tooltip title={item.name} placement="top">
					<span className="file-name cursor-pointer" onClick={() => handleDownloadFile(item)}>
						{getShortName(item.name, 20)}
					</span>
				</Tooltip>
				<span className="delete-icon cursor-pointer" onClick={() => removeDocumentById(item.id)}>
					<img src={`${process.env.PUBLIC_URL}/icons/delete24.svg`} alt="delete" />
				</span>
			</div>
		));

	const formatCostingHistoryValue = (item) => {
		const name = item?.createdByName || '';
		const rawDate = item?.modifiedAt || item?.priceLastModifiedAt || item?.createdAt;
		const formattedDate =
			rawDate && changeDateFormat(rawDate, 'YYYY-MM-DD', 'DD-MMM-YYYY') !== 'Invalid date'
				? changeDateFormat(rawDate, 'YYYY-MM-DD', 'DD-MMM-YYYY')
				: '';
		const meta = [name, formattedDate].filter(Boolean).join(', ');
		const qty = item?.moq != null ? item.moq : '';
		const cost = item?.totalCost != null ? item.totalCost : null;
		return `${meta} /${qty}/ ${cost != null ? `$${cost.toFixed(2)}` : ''}`.trim();
	};

	const renderCostingHistory = () => {
		if (!costingHistory.length) {
			return <li className="no-history regular-14 gray_dark_02">No history found</li>;
		}
		return costingHistory.map((item, index) => {
			const stepValue = getStepValue(item?.recostingStep);
			const label = item?.label ?? STEP_LABEL_MAP[stepValue] ?? stepValue ?? '';
			const value = item?.value ?? formatCostingHistoryValue(item);
			return (
				<li className="history-row d-flex" key={`option_costing_history_${index}`}>
					<span className="label regular-14 gray_dark_02">{label}</span>
					<span className="value regular-14 gray_dark_01 text-nowrap">{value}</span>
				</li>
			);
		});
	};

	const renderExtraDetailCells = (rowSpan) => (
		<>
			<td rowSpan={rowSpan} className="merged-row size-column">
				<input
					type="text"
					name="baseSize"
					autoComplete="off"
					value={inputData?.baseSize || ''}
					disabled={readOnly || isPoCostingDone}
					onChange={handleChange}
					onFocus={(event) => (prevTarget.current = event.target.value)}
					onBlur={(event) => {
						if (!isValidInput(prevTarget, event)) {
							resetAll();
							return;
						}
						saveBaseSize();
					}}
				/>
			</td>
			<td rowSpan={rowSpan} className="merged-row lead-time-column">
				<input
					type="number"
					name="leadTime"
					min={0}
					autoComplete="off"
					value={leadTime}
					disabled={readOnly || isPoCostingDone}
					placeholder="0"
					onKeyPress={validateNumber}
					onChange={handleLeadTime}
					onBlur={saveLeadTime}
				/>
				<span className="regular-12 gray_dark_02 days-text">Days</span>
			</td>
			<td
				rowSpan={rowSpan}
				className="merged-row details-column"
				onClick={() => !readOnly && !isPoCostingDone && setIsEditDetails(true)}
			>
				{isEditDetails ? (
					<textarea
						name="details"
						autoComplete="off"
						autoFocus
						value={inputData?.details || ''}
						disabled={readOnly || isPoCostingDone}
						onChange={handleChange}
						onFocus={(event) => {
							prevTarget.current = event.target.value;
							// place the cursor after the existing text
							const textLength = event.target.value.length;
							event.target.setSelectionRange(textLength, textLength);
						}}
						onBlur={(event) => {
							setIsEditDetails(false);
							if (!isValidInput(prevTarget, event)) {
								resetAll();
								return;
							}
							saveFabric();
						}}
					/>
				) : (
					<p className="gray_dark_01 regular-12 mb-0 cursor-pointer ml-2">{inputData?.details}</p>
				)}
			</td>
		</>
	);

	const optionTitle = (option?.label || '').replace(/^OP-/i, 'Option-');
	const optionReasons = (inputData?.costingOptionReasons || [])
		.map((reason) => reason?.name || reason?.constant)
		.filter(Boolean)
		.join(', ');

	return (
		<div className="costing-option-panel">
			{!!optionTitle && (
				<p className="costing-option-title px-3 regular-14 gray_dark_01">
					<span className="semibold-14">{optionTitle}:</span> {optionReasons}
				</p>
			)}
			<div className="form-group px-3">
				<CustomDropdown
					toolTipLength={20}
					type="addItem"
					title={'Select Factory'}
					isShowLabel={true}
					clearLabel={DESELECT_FACTORY_LABEL}
					onItemClick={(obj) => saveSupplier(obj?.id ? obj : {})}
					items={
						selectedSupplier?.id
							? [{ id: null, value: DESELECT_FACTORY_LABEL }, ...suppliers]
							: suppliers
					}
					onItemSearch={(value) => onSupplierSearch?.(`?name=${value}`)}
					selectedItem={selectedSupplier?.value || ''}
					dropdownIconPath={`${process.env.PUBLIC_URL}/icons/arrow_drop_up.svg`}
				/>
			</div>

			<div className="qty-unit-dtails-table px-3">
				<table className="table no-hover">
					<thead>
						<tr>
							<th className="qty-column">QTY</th>
							<th className="unit-cost-column">Unit cost</th>
							{isShowRecostingSection && <th className="target-price-column">Target price</th>}
							<th className="size-column">Size</th>
							<th className="lead-time-column">Lead time</th>
							<th className="details-column">Details</th>
						</tr>
					</thead>
					<tbody>
						{costList?.map((item, index) => (
							<tr key={`option_cost_list_${index}`}>
								<td className="qty-column">
									<input
										type="number"
										name="minQuantity"
										min={0}
										value={item?.minQuantity || ''}
										disabled={
											readOnly ||
											isPoCostingDone ||
											(index !== 0 && (!inputData?.moq || !inputData?.totalCost))
										}
										onKeyPress={validateNumber}
										onWheel={(event) => event.target.blur()}
										onChange={(event) => handleCostingChange(event, index)}
										onFocus={(event) => (prevTarget.current = event.target.value)}
										onBlur={(event) => {
											if (!isValidInput(prevTarget, event)) {
												resetAll();
												return;
											}
											saveCosting(event, index);
										}}
									/>
								</td>
								<td className="unit-cost-column">
									<div className="price-input d-flex align-items-center">
										{!!item?.price && <span className="currency-prefix">$</span>}
										<input
											type="number"
											name="price"
											min={0}
											step="0.01"
											value={item?.price || ''}
											disabled={
												readOnly ||
												isPoCostingDone ||
												!inputData?.moq ||
												(index !== 0 && !inputData?.totalCost)
											}
											onKeyPress={validateFloatNumber}
											onWheel={(event) => event.target.blur()}
											onChange={(event) => handleCostingChange(event, index)}
											onFocus={(event) => (prevTarget.current = event.target.value)}
											onBlur={(event) => {
												if (!isValidInput(prevTarget, event)) {
													resetAll();
													return;
												}
												saveCosting(event, index);
											}}
										/>
									</div>
								</td>
								{isShowRecostingSection && (
									<td className="target-price-column">
										<div className="price-input d-flex align-items-center">
											{!!item?.targetPrice && (
												<span className="currency-prefix">$</span>
											)}
											<input
												type="number"
												name="targetPrice"
												min={0}
												step="0.01"
												value={item?.targetPrice || ''}
												disabled={readOnly || !item?.id}
												onKeyPress={validateFloatNumber}
												onWheel={(event) => event.target.blur()}
												onChange={(event) => handleCostingChange(event, index)}
												onFocus={(event) => (prevTarget.current = event.target.value)}
												onBlur={(event) => {
													if (!isValidInput(prevTarget, event)) {
														resetAll();
														return;
													}
													saveTargetPrice(index);
												}}
											/>
										</div>
									</td>
								)}
								{index === 0 && renderExtraDetailCells(costList?.length || 1)}
							</tr>
						))}
					</tbody>
				</table>
			</div>

			<div className="attach-file px-3 pb-4">
				<div className="attachment-list d-flex justify-content-start align-items-center flex-wrap">
					<div
						className={`upload-box ${!option?.initialCostingId ? 'cursor-none' : 'cursor-pointer'}`}
					>
						<label htmlFor={`optionUpload_${option?.id}`}>
							<img src={`${process.env.PUBLIC_URL}/icons/upload-gray.svg`} alt="upload" />
							<span>Upload file</span>
						</label>
						<input
							type="file"
							className="file-upload d-none"
							id={`optionUpload_${option?.id}`}
							name="image"
							disabled={!option?.initialCostingId}
							onChange={(event) =>
								onMultipleFileUpload(event, 'PRODUCT_DESIGN', onUploadDocument)
							}
							multiple
						/>
					</div>
					{renderDocumentList()}
				</div>
			</div>

			<p className="regulr-12 gray_dark_02 last-modified">
				<span className="d-block">Costing by: {inputData?.priceLastModifiedBy}</span>
				{changeDateFormat(inputData?.priceLastModifiedAt, 'YYYY-MM-DD', 'DD MMM, YYYY') ===
				'Invalid date'
					? ''
					: 'Last modified: ' +
						convertDateToLocal(inputData?.priceLastModifiedAt, 'Do MMM YY, h:m a')}
			</p>

			{isShowRecostingSection && !!inputData?.totalCost && (
				<div className="costing-history-and-recosting d-flex justify-content-between align-items-start pt-4">
					<div className="dropdown costing-history-dropdown">
						{costingHistory.length > 0 && (
							<span
								className="regular-14 gray_dark_01 text-underline cursor-pointer"
								data-toggle="dropdown"
								role="button"
								aria-expanded="false"
							>
								History
							</span>
						)}
						<div className="dropdown-menu costing-history-menu shadow-8dp">
							<p className="semibold-14 gray_dark_01 title">{option?.label} costing history</p>
							<ul>{renderCostingHistory()}</ul>
						</div>
					</div>

					<div className="dropdown re-costing-dropdown">
						<button
							className="btn re-costing-toggle dropdown-toggle"
							type="button"
							data-toggle="dropdown"
							aria-expanded="false"
							disabled={isReCostingDisabled}
						>
							<span className="regular-14 gray_dark_01">Re-costing &amp; PO costing</span>
							<img src={`${process.env.PUBLIC_URL}/icons/arrow_drop_up.svg`} alt="dropdown" />
						</button>
						<ul className="dropdown-menu re-costing-menu shadow-8dp">
							{reCostingOptions.length ? (
								reCostingOptions.map((reCostingOption) => (
									<li
										className="regular-14 gray_dark_01"
										key={`option_re_costing_${reCostingOption.value}`}
										onClick={() => !readOnly && saveRecostingHistory(reCostingOption)}
									>
										{reCostingOption.label}
									</li>
								))
							) : (
								<li className="regular-14 gray_dark_02">Costing completed</li>
							)}
						</ul>
					</div>
				</div>
			)}

			<div className="costing-remarks" key={`option_remarks_${option?.id}`}>
				<textarea
					placeholder="Remarks"
					name="remarks"
					value={inputData?.remarks || ''}
					onChange={handleRemarks}
					onFocus={(event) => (prevTarget.current = event.target.value)}
					onBlur={(event) => {
						if (!isValidInput(prevTarget, event)) {
							resetAll();
							return;
						}
						saveRemarks();
					}}
					disabled={readOnly}
				/>
			</div>
		</div>
	);
};

export default CostingOptionPanel;
